'use client';

import React, { useEffect, useState } from 'react';
import useEncryptedLocalStorage from "@/lib/hooks/use-encrypted-local-storage";
import { ButtonOne, Image } from '@/components/shared';
import { Input, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { convertMarkdownBolds } from '@/lib/utils';
import ErrorMessage from '@/components/shared/forms/ErrorMessage';
import { LeaseActionFormErrors, LeaseActionFormValues } from "@/lib/types/shared/page.types";
import { useFormik } from 'formik';

type LocalStorageObject = {
    zipCode?: string | null,
}

type Props = {
    children: React.ReactNode;
}

const ValidateForm = (values: LeaseActionFormValues) => {
    const errors: LeaseActionFormErrors = {};
    
    // Zipcode Validation
    if (!values.zipCode) {
        errors.zipCode = 'zipcode.required';
    } else if (!/^[\d]{4} [A-Z]{2}$/i.test(values.zipCode)) {
        errors.zipCode = 'zipcode.invalid_format';
    } else if ((() => {
        const numberPart = parseInt(values.zipCode.substring(0, 4), 10);
        return numberPart < 3770 || numberPart > 3887;
        })()) {
        errors.zipCode = 'zipcode.invalid_zipcode';
    } else {
        if (errors.zipCode) delete errors.zipCode;
    }

    return errors;
}

const Blocker = ({ children }: Props) => {
    const t = useTranslations('pages.website_lease_action_barneveld.modal');

    const defaultLocalStorageObject: LocalStorageObject = {
        zipCode: null,
    }

    const [localStorageObject, setLocalStorageObject, isFirstLoad, usingInitialValue, reset] = useEncryptedLocalStorage<LocalStorageObject>('WebsiteLeaseActionBarneveld', defaultLocalStorageObject);
    const [localStorageObjectLoaded, setLocalStorageObjectLoaded] = useState(false);

    const [formSubmitting, setFormSubmitting] = useState(false);

    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    }

    useEffect(() => {
        setTimeout(() => {
            setLocalStorageObjectLoaded(true);
        }, 50);
    }, [isFirstLoad]);

    useEffect(() => {
        if (localStorageObjectLoaded) {
            if (!localStorageObject.zipCode) {
                setOpen(true);
            } else if ((() => {
                const numberPart = parseInt(localStorageObject.zipCode.substring(0, 4), 10);
                return numberPart < 3770 || numberPart > 3887;
                })()) {

                setLocalStorageObject({
                    zipCode: null,
                }, 'fallbackFunction');
                setOpen(true);
            }
        }
    }, [localStorageObjectLoaded]);

    // Form handlers
    const formik = useFormik({
        initialValues: {
            zipCode: '',
        },
          
        validate: ValidateForm,
        
        onSubmit: async (values) => {
            setFormSubmitting(true);

            setLocalStorageObject({
                zipCode: values.zipCode,
            }, 'submitFunction');

            setOpen(false);

            setFormSubmitting(false);
        }
    });

    type Event = {
        target: {
            name: string;
            value: string;
        };
    };

    const handleFormChange = (e: Event) => {
        formik.handleChange(e);
    }

    const handleFormBlur = (e: Event) => {
        formik.handleBlur(e);
    }

    const errors = formik.errors;
    const touched = formik.touched;
    const values = formik.values;

    return (
        <div>
            {!localStorageObject ? (
                <h1 className='text-5xl text-red-400'>Loading localStorageObject...</h1>
            ) : localStorageObject && !localStorageObject.zipCode ? (
                // localStorageObject loaded, zipcode not set
                // showing popup
                <div className='min-h-screen-mobile bg-white'>
                    <div id='website-lease-action-barneveld-modal' className={`fixed top-0 left-0 w-full h-screen-mobile z-50 flex justify-center items-center ${open ? 'block open' : 'hidden'}`}>
                        <div className='shadow-black-20 mx-5 mt-10 w-full md:w-2/3 h-fit max-h-screen-mobile overflow-y-auto package-modal-scrollbar bg-primary-gray-950 rounded-lg relative overflow-hidden' onClick={(e) => e.stopPropagation()}>
                            <div className='p-12'>
                                <Typography variant='h3' component='h2' className='capitalize font-bold text-center'>
                                    {t("title")}
                                </Typography>

                                <Typography variant='h4' component='h3' className='capitalize font-bold text-center' dangerouslySetInnerHTML={{ __html: t("subtitle") }} />

                                <Typography variant='body1' className='text-center'>
                                    {t("text")}
                                </Typography>
                            </div>


                            <form className="bg-white rounded-lg px-12 pt-10 pb-4 -mb-4 relative z-10 flex flex-col" onSubmit={formik.handleSubmit}>
                                <Typography variant='h4' component='h2' className='text-primary-gray-950 capitalize font-bold text-center'>
                                    {t("zipcode.title")}
                                </Typography>

                                <div className="field mt-4 mb-8 mx-auto flex flex-col items-center">
                                    <div className="w-fit transition rounded-xl bg-white border border-primary-gray-300">
                                        <Input className='w-fit center-text center-placeholder rounded-xl text-primary-gray-950 px-4 py-1' type="text" name="zipCode" placeholder={'1234 AA'} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.zipCode} required/>
                                    </div>
                                    <ErrorMessage>{errors.zipCode && touched.zipCode && t(errors.zipCode)}</ErrorMessage>
                                </div>

                                <ButtonOne disabled={formSubmitting || (errors.zipCode ? true : false)} variant='green' text={t("zipcode.button")} type='button' className='w-full' />
                            </form>

                            <Image
                                src="The_Grand_Office_yqlwk1"
                                alt="Confident Beuker"
                                width={709}
                                height={336}
                                className='rounded-lg w-full h-auto aspect-16/7 md:aspect-16/6 lg:aspect-16/4 object-cover block'
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {children}
                </>
            )}
        </div>
    );
}

export default Blocker;